// Maybe we should move this to its own folder to use it as a module => reusability++

import { concatReducer } from '@/utils/toolkit/array/concat.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import {
    type Comparer,
    createEntityAdapter,
    type Dictionary,
    type EntityState,
    type IdSelector,
    type Predicate,
    type Update
} from '@ngrx/entity';
import { createSelector, type MemoizedSelector } from '@ngrx/store';
import { groupBy } from 'lodash-es';

interface UpdateInformation<T> {
    original: T;
    updated: T;
    newEntityId: string | number;
    entityHasNewId: boolean;
}

type EntitySelectors<T, V> = {
    selectIds: (state: V) => string[] | number[];
    selectEntities: (state: V) => Dictionary<T>;
    selectAll: (state: V) => T[];
    selectTotal: (state: V) => number;
};

export interface MappedEntityAdapter<T, MapsEnum extends string> {
    selectId: IdSelector<T>;
    sortComparer?: false | Comparer<T>;

    getInitialState(): Required<MappedEntityState<T>>;
    getInitialState<S extends object>(state: S): Required<MappedEntityState<T>> & S;

    // state adapters
    addOne<S extends MappedEntityState<T>>(entity: T, state: S): S;
    addMany<S extends MappedEntityState<T>>(entities: T[], state: S): S;

    setAll<S extends MappedEntityState<T>>(entities: T[], state: S): S;
    setOne<S extends MappedEntityState<T>>(entity: T, state: S): S;
    setMany<S extends MappedEntityState<T>>(entities: T[], state: S): S;

    removeOne<S extends MappedEntityState<T>>(key: string, state: S): S;
    removeOne<S extends MappedEntityState<T>>(key: number, state: S): S;
    removeMany<S extends MappedEntityState<T>>(keys: string[], state: S): S;
    removeMany<S extends MappedEntityState<T>>(keys: number[], state: S): S;
    removeMany<S extends MappedEntityState<T>>(predicate: Predicate<T>, state: S): S;
    removeAll<S extends MappedEntityState<T>>(state: S): S;
    updateOne<S extends MappedEntityState<T>>(update: Update<T>, state: S): S;
    updateMany<S extends MappedEntityState<T>>(updates: Update<T>[], state: S): S;
    upsertOne<S extends MappedEntityState<T>>(entity: T, state: S): S;
    upsertMany<S extends MappedEntityState<T>>(entities: T[], state: S): S;

    getSelectors(): MappedEntitySelectors<T, MappedEntityState<T>, MapsEnum>;
    getSelectors<V>(selectState: (state: V) => MappedEntityState<T>): MappedEntitySelectors<T, V, MapsEnum>;
}

export interface MappedEntityState<T> extends EntityState<T> {
    maps: MapsState<T>;
}
export interface MapsState<T> {
    [mapName: string]: EntityIdListMap<T>;
}
export type EntityIdListMap<T> = StringKeyedMap<T> | NumberKeyedMap<T>;

export interface StringKeyedMap<T> {
    [propertyValue: string]: ReturnType<IdSelector<T>>[];
}

export interface NumberKeyedMap<T> {
    [propertyValue: number]: ReturnType<IdSelector<T>>[];
}

export interface MappedEntitySelectors<T, V, MapsEnum> {
    selectIds: (state: V) => string[] | number[];
    selectEntities: (state: V) => Dictionary<T>;
    selectAll: (state: V) => T[];
    selectTotal: (state: V) => number;
    selectMaps: MemoizedSelector<V, MapsState<T>>;
    selectMap: (mapName: MapsEnum) => MemoizedSelector<V, EntityIdListMap<T>>;
    selectEntitiesByMappedId: (mapName: MapsEnum, mappedId: ReturnType<IdSelector<T>>) => MemoizedSelector<V, T[]>;
    selectEntitiesByMappedIds: (mapName: MapsEnum, mappedIds: ReturnType<IdSelector<T>>[]) => MemoizedSelector<V, T[]>;
}

export function createMappedEntityAdapter<T, MapsEnum extends string>(options: {
    selectId: IdSelector<T>;
    sortComparer?: false | Comparer<T>;
    mapDefinitions: { selectMapId: IdSelector<T>; mapName: MapsEnum }[];
}): MappedEntityAdapter<T, MapsEnum> {
    const adapter = createEntityAdapter({ selectId: options.selectId, sortComparer: options.sortComparer });
    const mapDefinitions = options.mapDefinitions;
    const selectId = options.selectId;

    function getInitialState(object?: any): Required<MappedEntityState<T>> {
        return {
            ...{ ids: null, entities: null, maps: null }, // For better object display in devtools
            ...adapter.getInitialState(object ?? {}),
            maps: generateEmptyMaps()
        };
    }

    //////////// State reducer adapters ////////////

    function setAll<S extends MappedEntityState<T>>(entities: T[], state: S): S {
        const newState = adapter.setAll(entities, state);
        if (newState === state || !state.maps) return state; // no mutation => nothing to do

        // Reset the whole collection
        newState.maps = generateEmptyMaps();

        mapDefinitions.forEach(mapDefinition => {
            const map = newState.maps[mapDefinition.mapName];
            for (const entity of entities) {
                addEntityIdToMapMutably(map, mapDefinition.selectMapId(entity), selectId(entity));
            }
        });

        return newState;
    }

    function addMany<S extends MappedEntityState<T>>(entities: T[], state: S): S {
        const newState = adapter.addMany(entities, state);
        if (newState === state || !state.maps) return state; // no mutation => nothing to do

        newState.maps = {
            ...newState.maps
        };

        // Add to the whole collection
        mapDefinitions.forEach(mapDefinition => {
            newState.maps[mapDefinition.mapName] = { ...newState.maps[mapDefinition.mapName] };
            const map = newState.maps[mapDefinition.mapName];
            const grouped = groupBy(entities, mapDefinition.selectMapId);
            for (const mappedId in grouped) {
                if (!(map as any)[mappedId]) {
                    (map as any)[mappedId] = grouped[mappedId].map(entity => selectId(entity));
                } else {
                    (map as any)[mappedId] = (map as any)[mappedId].concat(grouped[mappedId].map(entity => selectId(entity)));
                }
            }
        });

        return newState;
    }

    function addOne<S extends MappedEntityState<T>>(entity: T, state: S): S {
        const newState = adapter.addOne(entity, state);
        if (newState === state || !state.maps) return state;

        mapDefinitions
            .filter(mapDefinition => definedValue(mapDefinition.selectMapId(entity)))
            .forEach(mapDefinition => {
                const newMap = { ...(newState.maps[mapDefinition.mapName] as any) } as EntityIdListMap<T>;
                addEntityIdToMap(newMap, mapDefinition.selectMapId(entity), selectId(entity));
                newState.maps = {
                    ...newState.maps,
                    [mapDefinition.mapName]: newMap
                };
            });

        return newState;
    }

    function removeOne<S extends MappedEntityState<T>>(key: string, state: S): S;
    function removeOne<S extends MappedEntityState<T>>(key: number, state: S): S;
    function removeOne<S extends MappedEntityState<T>>(key: ReturnType<IdSelector<T>>, state: S): S {
        const newState = adapter.removeOne(key as any, state); // as any because removeOne has two overloads
        if (newState === state || !state.maps) return state;

        mapDefinitions
            .filter(mapDefinition => {
                const id = state.entities[key];
                return id && definedValue(mapDefinition.selectMapId(id));
            })
            .forEach(mapDefinition => {
                const id = state.entities[key];
                if (!id) {
                    return;
                }
                const newMap = { ...(newState.maps[mapDefinition.mapName] as any) } as EntityIdListMap<T>;
                removeEntityIdFromMap(newMap, mapDefinition.selectMapId(id), key);
                newState.maps = {
                    ...newState.maps,
                    [mapDefinition.mapName]: newMap
                };
            });

        return newState;
    }

    function updateOne<S extends MappedEntityState<T>>(update: Update<T>, state: S): S {
        return updateMany([update], state);
    }

    function updateMany<S extends MappedEntityState<T>>(updates: Update<T>[], state: S): S {
        if (!updates || !updates.length) return state;
        const newState = adapter.updateMany(updates, state);
        if (newState === state || !state.maps) return state;

        const updateInformation: UpdateInformation<T>[] = updates
            .map(u => {
                const original: T | undefined = state.entities[u.id];

                if (!original) {
                    return;
                }

                const updated: T = Object.assign({}, original, u.changes);
                const newEntityId = selectId(updated);

                return {
                    original,
                    updated,
                    newEntityId,
                    entityHasNewId: newEntityId !== u.id
                };
            })
            .filter(isNotNullish);

        mapDefinitions.forEach(mapDefinition => {
            updateInformation.forEach(ui => {
                const oldMappedId = mapDefinition.selectMapId(ui.original);
                const newMappedId = mapDefinition.selectMapId(ui.updated);
                const entityHasNewMappedId = newMappedId !== oldMappedId;

                if (ui.entityHasNewId || entityHasNewMappedId) {
                    const newMap = { ...(newState.maps[mapDefinition.mapName] as any) } as EntityIdListMap<T>;
                    removeEntityIdFromMap(newMap, oldMappedId, ui.newEntityId);
                    addEntityIdToMap(newMap, newMappedId, ui.newEntityId);
                    newState.maps = {
                        ...newState.maps,
                        [mapDefinition.mapName]: newMap
                    };
                }
            });
        });
        return newState;
    }

    //////////// State selectors adapters ////////////

    function getSelectors<V>(selectState?: (state: V) => MappedEntityState<T>): MappedEntitySelectors<T, V, MapsEnum> {
        const entitySelectors: EntitySelectors<T, V> = (
            selectState ? adapter.getSelectors(selectState) : adapter.getSelectors()
        ) as EntitySelectors<T, V>;

        if (!selectState) {
            // mimic entitySelectors : if no state selector is provided, use root state
            selectState = state => state as any;
        }

        const selectMaps = createSelector(selectState, state => state.maps);
        const selectMap = (mapName: MapsEnum) => createSelector(selectMaps, maps => maps[mapName]);

        const selectEntitiesByMappedId = (mapName: MapsEnum, mappedId: ReturnType<IdSelector<T>>) =>
            createSelector(selectMap(mapName), entitySelectors.selectEntities, (map, entities) => {
                if (!(map as any)[mappedId]) return [];
                const items: any = (map as any)[mappedId].map((entityId: ReturnType<IdSelector<T>>) => entities[entityId]);
                if (options.sortComparer) {
                    items.sort(options.sortComparer);
                }
                return items;
            });

        const selectEntitiesByMappedIds = (mapName: MapsEnum, mappedIds: ReturnType<IdSelector<T>>[]) =>
            createSelector(selectMap(mapName), entitySelectors.selectEntities, (map, entities) => {
                const items = mappedIds
                    .map(mappedId => (map as any)[mappedId])
                    .filter(Array.isArray)
                    .reduce(concatReducer, [])
                    .map((entityId: ReturnType<IdSelector<T>>) => entities[entityId])
                    .filter(isNotNullish);
                if (options.sortComparer) {
                    items.sort(options.sortComparer);
                }
                return items;
            });

        return {
            ...entitySelectors,
            selectMaps,
            selectMap,
            selectEntitiesByMappedId,
            selectEntitiesByMappedIds
        };
    }

    //////////// Utils ////////////

    function addEntityIdToMap(map: EntityIdListMap<T>, entityMappedId: number | string, entityId: ReturnType<IdSelector<T>>) {
        (map as any)[entityMappedId] = [...((map as any)[entityMappedId] || []), entityId];
    }

    function addEntityIdToMapMutably(map: EntityIdListMap<T>, entityMappedId: number | string, entityId: ReturnType<IdSelector<T>>) {
        if (!(map as any)[entityMappedId]) {
            (map as any)[entityMappedId] = [];
        }
        (map as any)[entityMappedId].push(entityId);
    }

    function removeEntityIdFromMap(map: EntityIdListMap<T>, entityMappedId: number | string, entityIdToRemove: number | string) {
        (map as any)[entityMappedId] = (map as any)[entityMappedId].filter((entityId: any) => entityId !== entityIdToRemove);
        if (!(map as any)[entityMappedId].length) {
            delete (map as any)[entityMappedId];
        }
    }

    function generateEmptyMaps(): MapsState<T> {
        return mapDefinitions.reduce<MapsState<T>>((m, mapDefinition) => {
            m[mapDefinition.mapName] = {};
            return m;
        }, {});
    }

    function definedValue(value: any): boolean {
        return value !== null && value !== undefined;
    }

    return {
        selectId: adapter.selectId,
        sortComparer: adapter.sortComparer,

        getInitialState,

        addMany,
        addOne,

        setAll,
        setMany: adapter.setMany,
        setOne: adapter.setOne,

        removeAll: adapter.removeAll,
        removeMany: adapter.removeMany,
        removeOne,
        updateMany,
        updateOne,
        upsertMany: adapter.upsertMany,
        upsertOne: adapter.upsertOne,

        getSelectors
    };
}
